import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Button, Form, Pagination } from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { toast } from 'react-toastify';
import { FaFileExcel, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles } from '~/Styles/reactSelectStyles';
import { exportarEmExcel } from '~/Utils/Helpers';
import {
  buscarEmpreendimentosImoveisAlugados,
  listarImoveisAlugados,
} from '~/Services/Service';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// W-0697 — Imob > Administração > Consulta de Imóveis Alugados. Read-only.
// Fiel a UImovAlugado.pas: filtro de empreendimento (família) + grid + contador + export Excel.
// per_correcao: A=Anual / M=Mensal / outro=Sem Correção (AfterScroll do .pas).
// Abertas = qtd - pagas (calculado no front).

// chave = campo de ordenação; quando 'calc', usa o getter para o valor a ordenar.
const COLUNAS = [
  { campo: 'mapa1', titulo: 'Quadra', tipo: 'texto' },
  { campo: 'mapa2', titulo: 'Lote', tipo: 'texto' },
  { campo: 'nome', titulo: 'Imóvel', tipo: 'texto' },
  { campo: 'codigo_conta', titulo: 'Locatário', tipo: 'texto' },
  { campo: 'data_proposta', titulo: 'Data Proposta', tipo: 'data' },
  { campo: 'vencimento', titulo: 'Vencimento', tipo: 'data' },
  { campo: 'qtd', titulo: 'Total', tipo: 'int' },
  { campo: 'pagas', titulo: 'Pagas', tipo: 'int' },
  { campo: 'abertas', titulo: 'Abertas', tipo: 'int' },
  { campo: 'valor', titulo: 'Valor', tipo: 'decimal' },
  { campo: 'per_correcao', titulo: 'Per. Correção', tipo: 'texto' },
];

const formatarDecimal = valor => {
  if (valor === null || valor === undefined || valor === '') return '0,00';
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatarData = valor => {
  if (!valor) return '';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

const rotuloCorrecao = per =>
  per === 'A' ? 'Anual' : per === 'M' ? 'Mensal' : 'Sem Correção';

const rotuloLocatario = item => {
  const apelido = item.apelido_conta ? item.apelido_conta : '';
  return `${item.codigo_conta} - ${apelido}`;
};

// Valor comparável por coluna (W-0697 fix de ordenação): a chave bruta nem sempre
// serve para comparar — datas/decimais chegam como string/number e podem ser null,
// e Locatário/Per.Correção são exibidos formatados. Devolve number p/ data/int/decimal,
// string normalizada p/ texto, ou null quando ausente (tratado no comparador).
const valorOrdenacao = (item, coluna) => {
  if (coluna.campo === 'codigo_conta') return item.codigo_conta;
  if (coluna.campo === 'per_correcao') return rotuloCorrecao(item.per_correcao);
  const bruto = item[coluna.campo];
  if (bruto === null || bruto === undefined || bruto === '') return null;
  if (coluna.tipo === 'data') {
    const t = new Date(bruto).getTime();
    return isNaN(t) ? null : t;
  }
  if (coluna.tipo === 'int' || coluna.tipo === 'decimal') {
    const n = Number(bruto);
    return isNaN(n) ? null : n;
  }
  return String(bruto).toLowerCase();
};

// Comparador estável e type-aware. Nulos sempre ao final (asc e desc), espelhando
// o guard do RelatorioExtraContabil (W-0776). Strings via localeCompare pt-BR.
const compararValores = (va, vb, direction) => {
  const fator = direction === 'asc' ? 1 : -1;
  const aNulo = va === null;
  const bNulo = vb === null;
  if (aNulo && bNulo) return 0;
  if (aNulo) return 1;
  if (bNulo) return -1;
  if (typeof va === 'string' || typeof vb === 'string') {
    return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true }) * fator;
  }
  if (va < vb) return -1 * fator;
  if (va > vb) return 1 * fator;
  return 0;
};

const ITENS_POR_PAGINA = 20;

const ImoveisAlugados = () => {
  const [empreendimentosOptions, setEmpreendimentosOptions] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarEmpreendimentos();
    listar(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarEmpreendimentos = async () => {
    try {
      const res = await buscarEmpreendimentosImoveisAlugados();
      const lista = res ? res : [];
      setEmpreendimentosOptions(
        lista.map(e => ({
          value: e.codigo_familia,
          label: `${e.codigo_familia} - ${e.nome_da_familia}`,
        }))
      );
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar empreendimentos.');
    }
  };

  const listar = async codigoFamilia => {
    try {
      setCarregando(true);
      const res = await listarImoveisAlugados(codigoFamilia);
      setRegistros(res ? res : []);
      setPagina(1);
      setSortConfig({ key: null, direction: 'asc' });
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setCarregando(false);
    }
  };

  const onChangeEmpreendimento = selecionado => {
    setEmpreendimentoSelecionado(selecionado || null);
    listar(selecionado ? selecionado.value : null);
  };

  // Abertas = qtd - pagas (R07 do .pas), exposto como campo derivado para a grade/ordenação.
  const registrosComCalculo = useMemo(
    () =>
      registros.map(item => ({
        ...item,
        abertas: (item.qtd || 0) - (item.pagas || 0),
      })),
    [registros]
  );

  const registrosOrdenados = useMemo(() => {
    if (!sortConfig.key) return registrosComCalculo;
    const coluna = COLUNAS.find(c => c.campo === sortConfig.key);
    if (!coluna) return registrosComCalculo;
    const copia = [...registrosComCalculo];
    copia.sort((a, b) =>
      compararValores(
        valorOrdenacao(a, coluna),
        valorOrdenacao(b, coluna),
        sortConfig.direction
      )
    );
    return copia;
  }, [registrosComCalculo, sortConfig]);

  const ordenar = campo => {
    let direction = 'asc';
    if (sortConfig.key === campo && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: campo, direction });
    setPagina(1);
  };

  const iconeOrdenacao = campo => {
    if (sortConfig.key !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const valorCelula = (item, coluna) => {
    if (coluna.campo === 'codigo_conta') return rotuloLocatario(item);
    if (coluna.campo === 'per_correcao') return rotuloCorrecao(item.per_correcao);
    if (coluna.tipo === 'data') return formatarData(item[coluna.campo]);
    if (coluna.tipo === 'decimal') return formatarDecimal(item[coluna.campo]);
    return item[coluna.campo];
  };

  const exportarExcel = () => {
    if (!registrosOrdenados || registrosOrdenados.length === 0) return;
    const cabecalho = COLUNAS.map(c => c.titulo);
    const linhas = registrosOrdenados.map(item =>
      COLUNAS.map(c => valorCelula(item, c))
    );
    exportarEmExcel([cabecalho, ...linhas], 'imoveis-alugados.csv');
  };

  // Paginação client-side (mesmo padrão do ControleContratos W-0773).
  const totalPaginas =
    Math.ceil(registrosOrdenados.length / ITENS_POR_PAGINA) || 1;
  const registrosPaginados = registrosOrdenados.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => setPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i += 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === pagina}
          onClick={() => setPagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    return (
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ borderTop: '1px solid #dee2e6' }}
      >
        <small className="text-muted">
          {`Mostrando ${
            registrosPaginados.length > 0
              ? (pagina - 1) * ITENS_POR_PAGINA + 1
              : 0
          } até ${Math.min(
            pagina * ITENS_POR_PAGINA,
            registrosOrdenados.length
          )} de ${registrosOrdenados.length} registros`}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header title="Imóveis Alugados" />
      <Container fluid className="mt-3">
        <Card className="mb-3">
          <Card.Body>
            <Row className="align-items-end">
              <Col xs={12} md={6} lg={5}>
                <Form.Group className="mb-0">
                  <Form.Label>Empreendimento</Form.Label>
                  <Selector
                    isClearable
                    styles={customStyles}
                    placeholder="Todos os empreendimentos..."
                    noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                    options={empreendimentosOptions}
                    value={empreendimentoSelecionado}
                    onChange={onChangeEmpreendimento}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={7} className="text-md-right mt-3 mt-md-0">
                <span className="font-weight-bold mr-3">
                  Total de imóveis alugados - {registrosOrdenados.length}
                </span>
                <Button
                  variant="outline-success"
                  disabled={carregando || registrosOrdenados.length === 0}
                  onClick={exportarExcel}
                >
                  <FaFileExcel />
                  <span className="ml-2">Exportar Excel</span>
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {carregando && <Loading />}

        {!carregando && (
          <Card>
            <Card.Body className="table-responsive">
              {registrosOrdenados.length === 0 && (
                <div className="text-center text-muted py-3">
                  Nenhum imóvel alugado encontrado
                </div>
              )}
              {registrosOrdenados.length > 0 && (
                <>
                <Table className="table table-striped table-bordered table-hover table-sm">
                  <Thead>
                    <Tr className="text-center">
                      {COLUNAS.map(c => (
                        <Th
                          key={c.campo}
                          onClick={() => ordenar(c.campo)}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          {c.titulo}
                          {iconeOrdenacao(c.campo)}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody className="text-center">
                    {registrosPaginados.map((item, indice) => (
                      <Tr
                        key={`${(pagina - 1) * ITENS_POR_PAGINA + indice}-${item.codigo_ativo}-${item.codigo_proposta}`}
                      >
                        {COLUNAS.map(c => (
                          <Td key={c.campo}>{valorCelula(item, c)}</Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {renderizarPaginacao()}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ImoveisAlugados;
